import { html, property, PropertyValues, state } from 'lit-element';
import { classMap } from 'lit-html/directives/class-map';
import { set, get, cloneDeep, debounce } from 'lodash';

import type {
  CellEditRequestEvent,
  ColumnApi,
  EditableCallback,
  FirstDataRenderedEvent,
  GetRowIdParams,
  GridReadyEvent,
  IServerSideGetRowsParams,
  PaginationChangedEvent,
  RowEditingStoppedEvent,
  VirtualColumnsChangedEvent,
  IServerSideGetRowsRequest,
  ColDef,
  GetQuickFilterTextParams,
  GridApi,
  GridOptions,
  RowNode,
  FilterChangedEvent,
  ToolPanelVisibleChangedEvent,
  EditableCallbackParams,
} from 'ag-grid-community';
import { Grid, LicenseManager } from '@amzn/ag-grid-enterprise';
import baseStyles from '../../shared/base/base.lit.scss';
import {
  event,
  EventEmitter,
  KatLitElement,
  register,
} from '../../shared/base';
import { checkSlots } from '../../shared/slot-utils';

import styles from './data-grid.lit.scss';
import './actionBar/action-bar';
import './filter/filter';
import {
  _Emitter,
  _stringToArray,
  _stringToBoolean,
  _stringToNumber,
} from './utils';
import {
  BuilderConfig,
  DataGridConfigurationBuilder,
} from './data-grid-configuration-builder';
import {
  ActionColumnRenderer,
  ActionColumnRendererParams,
} from './action-column-renderer';
import { SelectionActionBarHandler } from './actionBar/selection-action-bar-handler';
import { EditActionBarHandler } from './actionBar/edit-action-bar-handler';
import { BaseCellRenderer } from './base-cell-renderer';
import { AdvancedFilterRendererProps } from './shared-types';
import { GridFilterModule } from './grid-filter-module';
import { GridSelectionModule } from './grid-selection-module';
import {
  CLASS_NAME,
  DEFAULT_GRID_OPTIONS,
  DEFAULT_COL_DEF,
  ACTION_COLUMN_ID,
} from './constants';
import getString, { strings } from './strings';
import { AutoWidthAdjustingCellRenderer } from './auto-width-adjusting-cell-renderer';

const GENERATED_UNIQUE_KEY_FIELD = '_dg__id';
export const PAGINATION_PREFIX_CLASS = 'kat-data-grid-pagination-label';

export interface DataGridColumn<TData = any> extends ColDef<TData> {
  editable?: boolean | EditableCallback;
  sortable?: boolean;
  wrapText?: boolean;
  sort?: 'asc' | 'desc';
  renderCustomContent?: (
    rootDiv: HTMLDivElement,
    row: any,
    index: number
  ) => void;
  comparator?: (valueA: TData, valueB: TData) => number;
  getQuickFilterText?: (params: GetQuickFilterTextParams) => string;
}

/**
 * This is copied out of @types/lodash because:
 *   1. Without it, data-grid.d.ts references @types/lodash, which may not exist in the consuming package.
 *   2. Including @types/lodash in our dependencies fixes this, but the version of @types/lodash depends on the version of TS in the consuming package. Installing a version of @types/lodash that's incompatible with your TS version breaks build.
 *   3. Redeclaring it here gets around those problems in a non-breaking way.
 */
interface KatDebounceFunc<T extends (...args: any[]) => any> {
  (...args: Parameters<T>): ReturnType<T> | undefined;
  cancel(): void;
  flush(): ReturnType<T> | undefined;
}

export interface PaginatedDataGridData {
  results: any[];
  totalResultsSize: number;
  page: number;
  pageSize: number;
  filteredResultsSize?: number;
  sortKey?: string;
  sortDirection?: string;
  error?: string;
  lastRowIndex?: number;
}

export interface ServerParamsRequest extends IServerSideGetRowsRequest {
  customFilter?: string[];
  customSearch?: {
    searchTerm?: string;
    searchOption?: string;
  };
  primaryKeyField?: string;
}

export interface DataGridServer {
  getRows: (params: ServerParamsRequest) => Promise<PaginatedDataGridData>;
}

export interface DataGridReadyEvent {
  configurationBuilder: DataGridConfigurationBuilder;
  api: GridApi;
}

/**
 * Re-exporting these for React wrappers. Without this, React wrappers have to declare ag-grid as
 * run time dep. Doning so causes BATS failures for some teams due to ag-grid's large size in node_modules.
 */
export type { GridOptions, GridApi, ColumnApi };

/**
 * @component {kat-data-grid} KatDataGrid Data Grid is a highly versatile grid component that offers a wide range of features. It is based on the AG Grid library, so the <a href="https://www.ag-grid.com/">AG Grid documentation</a> is a great resource to familiarize yourself with the available features. Once you have a good understanding of AG Grid, you can follow <a href="/design-system/tutorials/data-grid/">Katal's tutorial on the Data Grid component</a> to get started.
 * @requires GridOptions:../es/data-grid
 * @requires GridApi:../es/data-grid
 * @requires DataGridConfigurationBuilder:../es/data-grid
 * @requires DataGridServer:../es/data-grid
 * @requires PaginatedDataGridData:../es/data-grid
 * @requires DataGridColumn:../es/data-grid
 * @requires AdvancedFilterRendererProps:../es/data-grid
 * @guideline Do This is something you should do.
 * @guideline Dont This is something you should not do.
 * @status Production
 * @theme flo
 * @example Default  { "auto-height": "true", "client-side-data": "true", "page-size": "10", "rowData": [{ "id": "CMRVMLFVHDVHU0", "status": "Deployed", "logging": "on" }, { "id": "MHXUWVPLDTKTU1", "status": "Deployed", "logging": "off" }, { "id": "CPRIASGPWCOVM2", "status": "Deployed", "logging": "on" }], "columns": [{ "field": "id", "wrapText": false, "headerName": "Distribution ID", "sortable": true }, { "field": "status", "sortable": true, "editable": true }, { "field": "logging", "sortable": true, "editable": true }] }
 * @slot filter Contents will appear in the top-right corner.
 * @slot selection-actionbar Used for rendering custom controls / content in the action bar.
 * @slot custom-controls Content is rendered in the top right corner outside the grid.
 * @a11y {sr}
 * @a11y {contrast}
 * @a11y {keyboard}
 */
@register('kat-data-grid')
export class KatDataGrid extends KatLitElement {
  static get styles() {
    return [baseStyles, styles];
  }

  // #region PRIVATE PROPERTIES

  /**
   * used to store the grid instance once it is created. stored so that we can
   * call `_grid.destroy()` in the `disconnectedCallback()`
   */
  private _grid: Grid | undefined;

  // Container for AG Grid.
  private _gridContainer: HTMLElement | undefined;

  private _primaryKeyField?: string;
  private _pageSize: number | undefined;
  private _resultsPerPageOptions: number[] | undefined;

  /** a private event emitter used internally to coordinate row editing events */
  private _rowEditDone = new _Emitter<{
    rowId: string;
    type: 'cancelled' | 'saved';
  }>();

  /** a private event emitter used internally to coordinate bulk editing events */
  private _bulkEditDone = new _Emitter<'cancelled' | 'saved'>();

  // related to pagination. see `_appendPaginationComponent`
  private _paginationPrefixElement: HTMLElement | null = null;
  private _resultsPerPageDropdown: HTMLElement | null = null;
  private _paginationGtpInputElement: HTMLElement | null = null;
  private _goToPageElement: HTMLElement | null = null;

  private _isDestroyed = false;

  /**
   * the column definition for the provide "action column" that includes the edit
   * buttons
   */
  private _actionColumnConfig: DataGridColumn = {
    flex: 0,
    colId: ACTION_COLUMN_ID,
    headerName: getString('dg-action-column'),
    lockPosition: 'right',
    field: 'value',
    headerClass: 'sr-only',
    cellRenderer: ActionColumnRenderer,
    filter: false,
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    cellRendererParams: {
      isRowEditingEnabled: () => this.isRowEditingEnabled(),
      // note: this has to be a getter so the action column renderer can pull
      // the latest values from the current `this` instance
      getOptions: () => ({
        primaryKeyField: this.primaryKeyField,
        onEditRow: this.editRow,
        onCancelRow: this.cancelRowEdit,
        onSaveRow: this.saveRowEdit,
        customRenderer: this.actionColumnRenderer,
        isRowDisabled: this.isRowDisabled,
        isRowEditable: this.isRowEditable,
      }),
    } as ActionColumnRendererParams,
  };

  // eslint-disable-next-line compat/compat
  private _actionBarIntersectionObserver = new IntersectionObserver(
    ([entry]) => {
      const actionBar = this.shadowRoot.querySelector(
        `.${CLASS_NAME.ACTION_BAR_CONTAINER}`
      );

      if (!actionBar) return;

      if (
        !entry.isIntersecting ||
        window.innerHeight - this.getBoundingClientRect().bottom < 0
      ) {
        actionBar.classList.add('float');
      } else {
        actionBar.classList.remove('float');
      }
    },
    {
      root: null,
      threshold: [1],
      rootMargin: '-1px',
    }
  );

  /**
   * a simple getter that ensures `this.gridContainer` is always defined
   */
  private get gridContainer() {
    if (!this._gridContainer) {
      this._gridContainer = document.createElement('div');
      this._gridContainer.setAttribute('slot', 'grid-container');
    }
    if (!this.contains(this._gridContainer)) {
      this.appendChild(this._gridContainer);
    }
    return this._gridContainer;
  }

  // #endregion

  // #region TYPE METADATA
  /**
   * Ag-grid options are defined here: https://ag-grid.com/javascript-data-grid/grid-options/
   * @typedef GridOptions
   */
  /**
   * A class for configuring a data-grid, please see: https://code.amazon.com/packages/KatalComponents/blobs/heads/mainline/--/src/components/data-grid/data-grid-configuration-builder.ts
   * @typedef DataGridConfigurationBuilder
   */
  /**
   * A column definition, please see: https://code.amazon.com/packages/KatalComponents/blobs/heads/mainline/--/src/components/data-grid/data-grid.ts
   * @typedef DataGridColumn
   */
  /**
   * A filter render prop, please see https://code.amazon.com/packages/KatalComponents/blobs/heads/mainline/--/src/components/data-grid/shared-types.ts
   * @typedef AdvancedFilterRendererProps
   */
  /**
   *  Partial store paginated data
   *  @typedef {Object} PaginatedDataGridData
   *  @property {any[]} results
   *  @property {number} totalResultsSize
   *  @property {number} page
   *  @property {number} pageSize
   *  @property {number} filteredResultsSize Optional
   *  @property {string} sortKey Optional
   *  @property {string} sortDirection Optional
   *  @property {string} error Optional
   *  @property {number} lastRowIndex Optional
   */

  // #endregion

  // #region PUBLIC PROPERTIES

  /**
   * @deprecated a legacy alias to `gridOptions`.
   */
  get agGridOptions() {
    return this.gridOptions;
  }

  /** The field that is unique to each row of data. Required for ServerSide data. */
  @property({ attribute: 'primary-key-field', type: String })
  get primaryKeyField(): string {
    return this._primaryKeyField ?? GENERATED_UNIQUE_KEY_FIELD;
  }

  set primaryKeyField(value: string) {
    const oldValue = this._primaryKeyField;
    this._primaryKeyField = value;
    this.requestUpdate('primaryKeyField', oldValue);
  }

  /** Set to true if data is loaded all at once and not paginated by the server */
  @property({
    attribute: 'client-side-data',
  })
  clientSideData?: boolean;

  /** If true, columns will 'auto-size' themselves when they first load. Text will not be truncated and table will horizontally scroll if it's too wide. As the columns come into view they will auto-size themselves. */
  @property({
    attribute: 'always-auto-size-columns',
    reflect: true,
    converter: _stringToBoolean,
  })
  alwaysAutoSizeColumns?: boolean;

  /**
   * Page Size. MUST BE A VALUE IN THE RESULTS-PER-PAGE property.
   *
   * Default results per page are [10, 25, 50, 100]
   */
  @property({ attribute: 'page-size', reflect: true, type: Number })
  get pageSize(): number {
    if (
      typeof this._pageSize === 'number' &&
      this.resultsPerPageOptions.includes(this._pageSize)
    ) {
      return this._pageSize;
    }

    const middleIndex = Math.floor(this.resultsPerPageOptions.length / 2);
    return this.resultsPerPageOptions[middleIndex];
  }

  set pageSize(value: number) {
    const oldValue = this.pageSize;
    this._pageSize = value;
    if (!this.resultsPerPageOptions.includes(value)) {
      console.warn(
        `pageSize must be a value in resultsPerPageOptions. \`resultsPerPageOptions\` did not include value \`${value}\` .`
      );
    }

    this.requestUpdate('pageSize', oldValue);
  }

  /**
   * Set as true if you want to see Ag-Grid logs
   */
  @property({ attribute: 'debug', reflect: true, converter: _stringToBoolean })
  isDebugMode?: boolean;

  /** Override this if your action column width is not correct */
  @property({ attribute: 'action-col-min-width', reflect: true })
  actionColMinWidth?: number = 350;

  /**
   * CRITICAL: This string specifies the AG Grid enterprise license your team
   * will need to supply in order to use the grid. This can be temporarily
   * ignored for local pre-prod development.
   */
  @property({ attribute: 'license-key', type: String })
  licenseKey?: string = undefined;

  /** Configure the grid using this guided builder. Call apply() to submit your configuration. */
  @property({ attribute: false })
  gridConfigurationBuilder: DataGridConfigurationBuilder =
    new DataGridConfigurationBuilder(this);

  /** Message that tells the customer no items were found. */
  @property({ attribute: 'no-results-message' })
  noResultsMessage?: string = getString('dg-no-data');

  /** Message that tells the customer items exist but were filtered out. */
  @property({ attribute: 'no-filtered-results-message' })
  noFilteredResultsMessage?: string = getString('dg-no-search-results');

  /** If true, data will be paginated. */
  @property({
    reflect: true,
    converter: _stringToBoolean,
  })
  pagination?: boolean;

  /** If true, paginated data size is unknown. */
  @property({
    reflect: true,
    attribute: 'indefinite-results-size',
    converter: _stringToBoolean,
  })
  indefiniteResultsSize?: boolean;

  /** The text to show next to pagination numbers. e.g. "Movies 1-5 of 50" */
  @property({ attribute: 'pagination-prefix' })
  paginationPrefix?: string;

  /** If true, search input will be present */
  @property({
    attribute: 'search',
    reflect: true,
    converter: _stringToBoolean,
  })
  enableSearch?: boolean;

  /** Array of columns objects which directly correspond to the grid columns.  */
  @property({ attribute: true, type: Array })
  columns?: undefined | DataGridColumn[];

  /** Total Row Count */
  @property({ attribute: 'total-row-count', type: Number })
  totalRowCount?: number;

  /** The number of rows that pass the filters */
  @property({ type: Number, attribute: 'total-row-count-after-filter' })
  totalRowCountAfterFilter: number | undefined = undefined;

  /** If true, the grid will take only as much room as it needs to display the rows. It will not fill parent and will not scroll.
   *  If false, the grid will scroll if it cannot fit in the given parent. If the parent container is larger than the grid, the grid will stretch to fill parent.
   */
  @property({
    attribute: 'auto-height',
    reflect: true,
    type: Boolean,
    converter: _stringToBoolean,
  })
  autoHeight?: boolean = false;

  /** Use for clientSideData only. Optional property to control the loading overlay. Do not use unless you want to explicitly control the loading overlay*/
  @property({
    attribute: 'is-loading',
    reflect: true,
    type: Boolean,
    converter: _stringToBoolean,
  })
  isLoading?: boolean = false;

  /** The message to show during loading */
  @property({
    attribute: 'loading-text',
    reflect: true,
    type: String,
  })
  loadingText?: string;

  /** Optional way to manually control grid height. Must be a valid CSS declaration. */
  @property({ attribute: 'height' })
  height?: string = undefined;

  /** Optional way to manually control grid width. Must be a valid CSS declaration. */
  @property({ attribute: 'width' })
  width?: string = undefined;

  /** If true, shows a dropdown by the search box to enable specific column searching */
  @property({
    attribute: 'show-search-drop-down',
    reflect: true,
    converter: _stringToBoolean,
  })
  showSearchDropDown?: boolean;

  /** If true, bulk edit button will show. */
  @property({
    attribute: 'bulk-edit',
    reflect: true,
    converter: _stringToBoolean,
  })
  bulkEdit = false;

  /** If true, row edit buttons will show if there are editable columns. */
  @property({
    attribute: 'row-edit',
    reflect: true,
    converter: _stringToBoolean,
  })
  rowEdit = false;

  /** If true, enables nested content */
  @property({
    attribute: 'master-detail',
    reflect: true,
    converter: _stringToBoolean,
  })
  masterDetail = false;

  /** Set the height of detail row when implementing master/detail */
  @property({
    attribute: 'detail-row-height',
    reflect: true,
    converter: _stringToNumber,
    type: Number,
  })
  detailRowHeight?: number | undefined;

  /** If false, filtered row count and total row count can be changed for filter/search (using setTotalRowCount() and setFilteredRowCount()). Default value true */
  @property({
    attribute: 'auto-filter-row-count',
    reflect: true,
    converter: _stringToBoolean,
  })
  autoFilterRowCount = true;

  /** True if you want the 'showAllFilterButton' to exist. */
  @property({
    attribute: 'all-filter',
    reflect: true,
    converter: _stringToBoolean,
  })
  showAllFilterButton = false;

  /** Full-store row data */
  @property({ attribute: 'row-data' })
  rowData?: any[] = undefined;

  /** The server object that supplies backend paginated data. Should implement getRows(params)=>RowData[]. */
  @property({ attribute: false })
  server?: DataGridServer;

  /** Selection can be single or multi selection. */
  @property({ reflect: true })
  selection?: 'single' | 'multi' | 'none' = 'none';

  /** Array of all searchable columns to show in the drop down. Accepts either an array of field values or an array of {displayName, field} pairs. Field value should match your column configuration. */
  @property({ attribute: 'search-options' })
  searchOptions?: (string | { name: string; value: string })[] = undefined;

  /** Current search term. */
  @property({ attribute: 'search-term' })
  searchTerm?: string = '';

  /** Current search placeholder. */
  @property({ attribute: 'search-placeholder' })
  searchPlaceholder?: string;

  /**
   * This object is sent to AG Grid. Any AG Grid functionality you wish to use
   * can be enabled/controlled through this object. Only use this object to
   * configure features you cannot set via other properties.
   *
   * Note: this object reference is preserved throughout the lifetime of this
   * KatDataGrid instance. This is because AG Grid keeps the original reference
   * of the gridOptions object and allows you to mutate it after the grid has
   * been created.
   *
   * If you try to set the gridOptions property to a new object, it will simply
   * `Object.assign` the new object into the existing object.
   */
  @property({ attribute: false })
  get gridOptions(): GridOptions {
    return this._gridOptions;
  }

  set gridOptions(incomingOptions: GridOptions) {
    // remove the `columnApi` and `api` properties from the incoming options to
    // prevent the existing instances from being replaced. these should not be
    // user overridable
    const { api, columnApi, ...rest } = incomingOptions;

    // use `Object.assign` to merge the incoming options into the existing
    // options and preserve the existing instance
    Object.assign(this._gridOptions, rest);

    // trigger a re-render from since we customized the setter
    this.requestUpdate('gridOptions');
  }

  /** This promise returns the Ag-Grid Options object that controls the underlying Ag-Grid. */
  @property({ attribute: false, type: Promise })
  gridReady: Promise<
    GridOptions & {
      api: NonNullable<GridOptions['api']>;
      columnApi: NonNullable<GridOptions['columnApi']>;
    }
  > = new Promise(resolve =>
    this.addEventListener('gridReady', () =>
      resolve(
        // we assert these as defined because AG Grid should put them there
        // after the grid is ready
        this.gridOptions as GridOptions & {
          api: NonNullable<GridOptions['api']>;
          columnApi: NonNullable<GridOptions['columnApi']>;
        }
      )
    )
  );

  /** Results per page is an array of options for page size. Defaults to [10, 25, 50, 100] */
  @property({
    attribute: 'results-per-page-options',
    reflect: true,
    type: [Number],
    converter: _stringToArray,
  })
  get resultsPerPageOptions(): number[] {
    return this._resultsPerPageOptions || [10, 25, 50, 100];
  }

  set resultsPerPageOptions(value: number[]) {
    const oldValue = this.resultsPerPageOptions;
    this._resultsPerPageOptions = value;
    this.requestUpdate('resultsPerPageOptions', oldValue);
  }

  /** If true, user can add filters in the additional filters slot */
  @property({
    attribute: 'additional-filter',
    reflect: true,
    converter: _stringToBoolean,
  })
  additionalFilter?: boolean;

  /** Action column renderer defines what shows up in the action column renderer  *** REACT USERS: Make sure you are not recreating this callback for each render cycle, or you will be in an infinite loop. *** */
  @property({ attribute: false })
  actionColumnRenderer?: (rowData: any) => any;

  /** Renders the advanced filter pane */
  @property({ attribute: false })
  advancedFilterRenderer?: AdvancedFilterRendererProps;

  /** User implements this function to decide which rows are editable. isRowEditable takes a single row's data and returns true or false *** REACT USERS: Make sure you are not recreating this callback for each render cycle, or you will be in an infinite loop. *** */
  @property({ attribute: false })
  isRowEditable?: (row: any) => boolean;

  /** User implements this function to decide which rows are disabled. isRowEditable takes a single row's data and returns true or false *** REACT USERS: Make sure you are not recreating this callback for each render cycle, or you will be in an infinite loop. *** */
  @property({ attribute: false })
  isRowDisabled?: (row: any) => boolean;

  /** Set as true to save any edit when user clicks outside of cell. Only applies to single row editing, not bulk editing. Defaults to false. */
  @property({ attribute: false })
  saveOnExit = false;

  // #endregion

  // #region INTERNAL STATE

  @state()
  editMode: 'inactive' | 'row' | 'bulk' = 'inactive';

  @state()
  _selectedRows?: RowNode[] = [];

  @state()
  actionBarWidth?: string = undefined;

  @state()
  currentPage?: number = undefined;

  @state()
  filterIsPresent?: boolean = false;

  @state()
  displayedRowCount: number | undefined;

  @state()
  selectedSearchOption?: string = undefined;

  @state()
  serverSideSearchApplied?: boolean = false;

  @state()
  changedRows: Record<string, any> = {};

  @state()
  originalRows: Record<string, any> = {};

  @state()
  toolPanelVisible = false;

  // #endregion

  // #region EVENTS

  /** Fired when grid is ready to configure */
  @event('gridReady', false)
  _dataGridReady: EventEmitter<{
    configurationBuilder: DataGridConfigurationBuilder;
    api: GridApi;
  }>;

  /** Fired when selection changed */
  @event('selectionChanged', false)
  _selectionChanged: EventEmitter<{
    selectedRows: any[];
    selectedNodes: any[];
  }>;

  /** Fires when filters are cleared so that custom slotted filters can be updated. */
  @event('filtersCleared', false)
  _filtersCleared: EventEmitter;

  /** Fired when filters are changed */
  @event('customFilterChanged', false)
  _customFilterChanged: EventEmitter<{
    'applied-filters': string[];
  }>;

  /** Fired when rows are edited */
  @event('rowsEdited', false)
  _rowsEdited: EventEmitter<{
    changedRows: Record<string, any>;
  }>;

  // #endregion

  // #region SUB MODULES

  filterModule: GridFilterModule = new GridFilterModule(this);
  selectionModule: GridSelectionModule = new GridSelectionModule(this);

  // #endregion

  // #region PUBLIC METHODS

  //adds new filter into externalFilterStore and calls _applyCurrentFilterInStore
  //for serverside only needs FilterName
  applyCustomFilter(
    filterName: string,
    callBack: ((node: RowNode) => boolean) | undefined = undefined
  ) {
    this.filterModule.externalFilterStore[filterName] = callBack;
    this.filterModule.applyCurrentFilterInStore();
  }

  applySingleCustomFilter(
    filterName: string,
    callBack?: (node: any) => boolean
  ) {
    this.filterModule.externalFilterStore = { [filterName]: callBack };
    this.filterModule.applyCurrentFilterInStore();
  }

  applyConfig({
    primaryKeyField,
    columns,
    isRowEditableCondition,
    server,
    enableRowEdit = false,
    saveOnExit,
    actionColumnRenderer,
    isRowDisabledCondition,
    agGridOptionValues,
  }: BuilderConfig) {
    if (primaryKeyField) {
      this.primaryKeyField = primaryKeyField;
    }
    if (actionColumnRenderer) {
      this.actionColumnRenderer = actionColumnRenderer;
    }
    this.rowEdit = enableRowEdit ? enableRowEdit : this.rowEdit;
    this.saveOnExit = saveOnExit;
    this.saveOnExit = saveOnExit;

    this.isRowEditable = isRowEditableCondition;
    this.isRowDisabled = isRowDisabledCondition;

    if (agGridOptionValues) {
      this.gridOptions = agGridOptionValues;
    }

    if (server) {
      this.setServerSideServer(server);
    }
    this.columns = columns;
  }

  clearAllCustomFilters = this.filterModule.clearAllCustomFilters;

  clearCustomFilters = this.filterModule.clearCustomFilters;

  clearCustomFilter = this.filterModule.clearCustomFilter;

  getSelections() {
    const { api } = this.gridOptions;
    if (!api) return [];

    const data = api.getSelectedRows();
    return data.map(data => ({
      data: data.data,
    }));
  }

  setAdvancedFilterRenderer(arg: AdvancedFilterRendererProps) {
    this.advancedFilterRenderer = arg;
  }

  setRowData(rowData: any[], manuallyTurnOffLoading?: boolean) {
    if (manuallyTurnOffLoading) {
      this.isLoading = false;
    }
    this.rowData = rowData;
  }

  async setServerSideServer(server: DataGridServer) {
    const { api } = await this.gridReady;
    api.setServerSideDatasource(this._createDatasource(server));
  }

  setTotalRowCount(count: number) {
    this.totalRowCount = count;
  }

  setFilteredRowCount(count: number) {
    this.totalRowCountAfterFilter = count;
  }

  selectDisplayedRows = this.selectionModule.selectDisplayedRows;

  isRowEditingEnabled = () => {
    const { api } = this.gridOptions;
    if (!api) return false;

    return !!(
      this.rowEdit && this.columns?.some(c => 'editable' in c && c.editable)
    );
  };

  /**
   * Enables row editing on the given RowNode. Returns a promise that resolves
   * after the edit has finished. The `rowsEdited` event will fire if the row's
   * save button as pressed or if `saveOnExit` is true
   */
  editRow = async (node: RowNode) => {
    const { api } = await this.gridReady;
    this.editMode = 'row';

    try {
      const editableColumns = this.columns?.filter(
        c => !!c.editable && !c.hide && !c.rowGroup
      );
      if (!editableColumns?.length) return;
      const lastEditableColumn = editableColumns[editableColumns.length - 1];

      const rowIndex = api.getRowNode(node.id)?.rowIndex;
      if (typeof rowIndex !== 'number') return;

      api.startEditingCell({
        rowIndex,
        colKey: lastEditableColumn.field,
      });

      const { cellEditRequests, type } = await this._rowEditingStopped(node.id);

      // if the user clicked the cancelled button before rowEditingStopped then early return
      if (type === 'cancelled') return;
      // if the users didn't click save and `saveOnExit` is falsy then also early return
      if (type !== 'saved' && !this.saveOnExit) return;

      // create new row data by reducing over the `cellEditRequests`
      const newData = cellEditRequests.reduce((acc, { colDef, newValue }) => {
        const { field } = colDef;
        if (!field) return acc;

        // NOTE: we use lodash `get` and `set` because AG Grid allows nested
        // fields via a path string with a `.`
        const oldValue = get(node.data, field);
        if (oldValue === newValue) return acc;

        return set(
          // we clone the acc deeply to make this change immutably
          cloneDeep(acc),
          field,
          newValue
        );
      }, node.data);

      // nothing changed; early return
      if (newData === node.data) return;

      node.setData(newData);
      this._rowsEdited.emit({ changedRows: { [node.id]: newData } });
    } finally {
      this.editMode = 'inactive';
    }
  };

  /**
   * Stops and commits the current row edit if the given rowId matches the row
   * being edited. This will fire `rowsEdited` event.
   */
  saveRowEdit = async (rowId: string) => {
    const { api } = await this.gridReady;

    // this will signal to `_rowEditingStopped` that the user clicked "Save"
    this._rowEditDone.emit({ rowId, type: 'saved' });
    // calling `api.stopEditing()` will eventually cause the `rowEditingStopped`
    // to fire which will resolve the promise `_rowEditingStopped()` returns
    api.stopEditing();
  };

  /**
   * Stops and cancels the current row edit if the given rowId matches the row
   * being edited.
   */
  cancelRowEdit = async (rowId: string) => {
    const { api } = await this.gridReady;

    // this will signal to `_rowEditingStopped` that the user clicked "Cancel"
    this._rowEditDone.emit({ rowId, type: 'cancelled' });
    // calling `api.stopEditing()` will eventually cause the `rowEditingStopped`
    // to fire which will resolve the promise `_rowEditingStopped()` returns
    api.stopEditing();
  };

  /**
   * enables the bulk editing mode. returns a promise that resolves when bulk
   * editing has finished when the user clicks the bulk edit "Save" or "Cancel"
   * buttons. This method keeps a list of changes made to the grid and if the
   * user ends bulk editing by saving, it will emit a `rowsEdited` event with
   * any data that has changed. If the user ends bulk editing by cancelling, any
   * temporary changes made to row data will be restored and the `rowsEdited`
   * event will not fire.
   */
  editBulk = async () => {
    const { api, columnApi } = await this.gridReady;

    // if already bulk editing, no-op
    if (this.editMode === 'bulk') return;

    this.editMode = 'bulk';

    const snapshots: Record<string, unknown> = {};
    const changeTracker: Record<string, unknown> = {};

    const handleCellEditRequest = ({
      node,
      column,
      colDef,
      newValue,
    }: CellEditRequestEvent) => {
      const { field } = colDef;

      if (!field) {
        console.warn('Found a colDef without a field. This is not supported.');
        return;
      }

      if (!(node.id in snapshots)) {
        snapshots[node.id] = cloneDeep(node.data);
      }

      const columnId = column.getId();
      const rowId = node.id;
      const path = JSON.stringify([rowId, columnId]);

      changeTracker[path] = newValue;
      node.setDataValue(columnId, newValue);
    };

    try {
      this.selectionModule.deselectAllRows();

      columnApi.setColumnVisible(ACTION_COLUMN_ID, false);
      api.refreshCells({
        force: true,
        columns: [ACTION_COLUMN_ID],
      });

      api.addEventListener('cellEditRequest', handleCellEditRequest);

      // await for the user to click "save" or "cancelled"
      const result = await this._bulkEditFinished();

      if (result === 'saved') {
        // commit
        const changedRows = Object.entries(changeTracker).reduce<
          Record<string, unknown>
        >((acc, [path, newValue]) => {
          const [rowId, columnId] = JSON.parse(path);
          const { field } = columnApi.getColumn(columnId).getColDef();

          const snapshot = snapshots[rowId];
          const oldValue = get(snapshot, field);

          if (newValue === oldValue) return acc;

          acc[rowId] = set(
            // eslint-disable-next-line @typescript-eslint/ban-types
            cloneDeep((acc[rowId] ?? snapshot) as object),
            field,
            newValue
          );

          return acc;
        }, {});

        this._rowsEdited.emit({ changedRows });
      } else {
        // revert
        for (const path of Object.keys(changeTracker)) {
          const [rowId] = JSON.parse(path);
          if (rowId in snapshots) {
            api.getRowNode(rowId).setData(snapshots[rowId]);
          }
        }
      }
    } finally {
      this.editMode = 'inactive';
      api.removeEventListener('cellEditRequest', handleCellEditRequest);
      columnApi.setColumnVisible(ACTION_COLUMN_ID, true);
      api.refreshCells({
        force: true,
        columns: [ACTION_COLUMN_ID],
      });
    }
  };

  /**
   * Cancels a bulk edit if any are in progress
   */
  cancelBulkEdit = () => this._bulkEditDone.emit('cancelled');

  /**
   * Saves a bulk edit if any are in progress
   */
  saveBulkEdit = () => this._bulkEditDone.emit('saved');

  isRowGroupPresent() {
    return !!this.columns?.some(c => 'rowGroup' in c && c.rowGroup);
  }

  // #endregion

  // #region EVENT HANDLERS

  handlePaginationChanged = ({ api, newPage }: PaginationChangedEvent) => {
    if (!this.clientSideData && this.editMode === 'bulk' && newPage) {
      this.cancelBulkEdit();
    }

    this.currentPage = api.paginationGetCurrentPage() + 1; // page number from ag grid starts from 0
  };

  handleFilterChanged = ({ api }: FilterChangedEvent) => {
    if (this.clientSideData) {
      if (this.isLoading) {
        api.showLoadingOverlay();
      } else {
        if (api.getRenderedNodes().length) {
          api.hideOverlay();
        } else {
          api.showNoRowsOverlay();
        }
      }
    }

    if (this.autoFilterRowCount) this._recalculateFilteredRowCount();
    this.filterModule.handleAdvancedFilterVisual(this);

    // this should not be needed, this is a temp fix for native column header filter from ag-grid
    if (!this.clientSideData) api.refreshServerSide();
  };

  handleDisplayedRowsChanged = () => {
    const { api } = this.gridOptions;
    if (!api) return;

    this.displayedRowCount = api.getRenderedNodes().length;
    if (this.autoFilterRowCount) this._recalculateFilteredRowCount();
    this.selectionModule.handleNewRowsSelectionServerside();
  };

  handleGridReady = ({ api }: GridReadyEvent) => {
    const event: DataGridReadyEvent = {
      configurationBuilder: new DataGridConfigurationBuilder(this),
      api,
    };
    this._dataGridReady.emit(event);
  };

  getQuickFilterText = ({ colDef, value }: GetQuickFilterTextParams) => {
    if (this.selectedSearchOption) {
      if (colDef.field === this.selectedSearchOption) {
        return value;
      }
      return '';
    }
    return value;
  };

  getRowId = ({ data }: GetRowIdParams) => data[this.primaryKeyField];

  /**
   * Calls `autoSizeColumns` with the current list of displayed columns if
   * `alwaysAutoSizeColumns` is enabled
   */
  // note: we use lodash `debounce` here instead of the `debounce` decorator
  // because we need to bind the method to the instance and the current
  // implementation of `@debounce` does not support that usage
  handleVirtualColumnsChanged = debounce(
    ({ columnApi }: VirtualColumnsChangedEvent) => {
      // don't auto-size columns if `alwaysAutoSizeColumns` is false
      if (!this.alwaysAutoSizeColumns) return;

      columnApi.autoSizeColumns(
        // only auto-size the columns that are currently visible
        columnApi.getAllDisplayedColumns(),
        // skip headers when auto-sizing
        true
      );
    },
    100
  ) as KatDebounceFunc<({ columnApi }: VirtualColumnsChangedEvent) => void>;

  /**
   * used to ensure all the columns are being auto-sized correctly on the first
   * render. this is related to the `AutoWidthAdjustingCellRenderer` workaround
   * for sizing web components and other elements that don't show their full
   * width on the first render
   */
  handleFirstDataRendered = async ({
    columnApi,
    api,
  }: FirstDataRenderedEvent) => {
    // wait a few animation frames to ensure the grid is fully rendered
    for (let i = 0; i < 5; i++) {
      await new Promise(resolve => requestAnimationFrame(resolve));
    }

    if (this._isDestroyed) return;

    if (this.alwaysAutoSizeColumns) {
      columnApi.autoSizeAllColumns(
        true // skip headers
      );
    } else {
      api.sizeColumnsToFit();
    }
  };

  handleToolPanelVisibleChanged = ({ api }: ToolPanelVisibleChangedEvent) => {
    this.toolPanelVisible = api.isToolPanelShowing();
  };

  // #endregion

  // #region LIT LIFECYLE METHODS

  firstUpdated() {
    if (this.licenseKey) {
      LicenseManager.setLicenseKey(this.licenseKey);
    }

    super.firstUpdated();

    this._actionBarIntersectionObserver.observe(
      this.shadowRoot.querySelector(`.${CLASS_NAME.ACTION_BAR_CONTAINER}`)
    );
  }

  renderControlBar() {
    let displayedSearchOptions: { name: string; value: string }[];

    if (this.searchOptions) {
      displayedSearchOptions = this.searchOptions.map(opt => {
        if (typeof opt === 'string') {
          return {
            name: opt,
            value: opt,
          };
        }

        return {
          name: opt.name,
          value: opt.value,
        };
      });
    } else {
      displayedSearchOptions =
        this.columns
          ?.filter(col => col.colId !== ACTION_COLUMN_ID)
          .map(col => ({
            name: col.headerName ? col.headerName : col.field,
            value: col.field,
          })) || [];
    }

    displayedSearchOptions.unshift({ name: getString('dg-all'), value: 'all' }); //Adding All in dropDown

    const slots = checkSlots(this);

    const hasContent =
      !this.filterModule.isFilterBarEmpty(this) ||
      this.bulkEdit ||
      slots['custom-controls'] ||
      this.showAllFilterButton;

    if (!hasContent) return null;

    return html`<div class="${CLASS_NAME.CONTROL_BAR_CONTAINER}">
      ${this.filterModule?.isFilterBarEmpty(this)
        ? null
        : html`<div class="filters-container">
            <kat-data-grid-filter
              @search-term-changed=${this.filterModule.handleSearch}
              @clear-all-filter-search=${this.filterModule
                .handleClearAllFilterAndSearch}
              .additionalFilter=${this.additionalFilter}
              .clientSideData=${this.clientSideData}
              .showSearchDropDown=${this.showSearchDropDown}
              .totalRowCount=${this.totalRowCount}
              .totalRowCountAfterFilter=${this.totalRowCountAfterFilter}
              .enableSearch=${this.enableSearch}
              .isFilterApplied=${this.filterModule.isFilterApplied()}
              .advancedFilterRenderer=${this.advancedFilterRenderer}
              .katDataGrid=${this}
              .appendAdvancedFilterComponents=${this.filterModule
                .appendAdvancedFilterComponents}
              .searchOptions=${displayedSearchOptions}
              .searchTerm=${this.searchTerm}
              .searchPlaceholder=${this.searchPlaceholder}
            >
              <slot
                name="filter"
                slot="filter"
                @slotchange=${this.requestUpdate.bind(this)}
              ></slot>
            </kat-data-grid-filter>
          </div>`}
      <div class="custom-controls-container">
        ${this.showAllFilterButton
          ? html` <div class="all-filter-button">
              <kat-button
                @click="${this.filterModule.handleAllFilterButtonClicked}"
                variant="utility"
                part="all-filters-button"
              >
                <span class="all-filters-content">
                  <kat-icon
                    name="all_filters"
                    size="tiny"
                    slot="icon"
                  ></kat-icon>
                  <span>${getString('dg-all-filters')}</span>
                </span>
              </kat-button>
            </div>`
          : null}
        ${this.bulkEdit
          ? html`
              <div class="static-buttons">
                <kat-button
                  class=${classMap({
                    'bulk-edit': true,
                    'with-custom-controls': slots['customControls'],
                  })}
                  label="${getString('dg-bulk-edit')}"
                  .disabled=${this.editMode === 'row'}
                  @click="${this.editBulk}"
                  part="bulk-edit-button"
                ></kat-button>
              </div>
            `
          : null}
        <slot name="custom-controls"></slot>
      </div>
    </div>`;
  }

  renderActionBar() {
    return html`
      <span class="${CLASS_NAME.ACTION_BAR_CONTAINER}">
        ${this.editMode === 'bulk'
          ? html`
              <kat-data-grid-action-bar
                .handler=${new EditActionBarHandler()}
                @cancel-bulk-edit=${this.cancelBulkEdit}
                @save-bulk-edit=${this.saveBulkEdit}
              ></kat-data-grid-action-bar>
            `
          : null}
        ${this._selectedRows?.length && this.selection === 'multi'
          ? html`
              <kat-data-grid-action-bar
                class="selection-action-bar"
                .handler=${new SelectionActionBarHandler(
                  this._selectedRows.length
                )}
                @action-bar-closed=${this.selectionModule.deselectAllRows}
              >
                <slot
                  name="selection-actionbar"
                  slot="selection-actionbar"
                ></slot>
              </kat-data-grid-action-bar>
            `
          : null}
      </span>
    `;
  }

  render() {
    this._updateAgGridContainer();

    return html`
      <div class="kat-data-grid-container">
        ${this.renderControlBar()}
        <slot name="grid-container"></slot>
        ${this.renderActionBar()}
      </div>
    `;
  }

  async updated(changedProperties: PropertyValues) {
    if (!this._grid) {
      /**
       * https://sim.amazon.com/issues/KDS-5021
       *
       * Only initialize the grid the first time one of these properties are set. Some gridOptions
       * cannot be changed after initalization (or at least changing them does not have the desired effect).
       * This is a best effort at initalizing the grid with the expected properties. It's also valid to not
       * pass any gridOptions, in which case setting columns is the appropriate trigger for grid creation.
       */
      if (
        changedProperties.has('gridOptions') ||
        changedProperties.has('columns')
      ) {
        this._grid = new Grid(this.gridContainer, this.gridOptions);
        this._appendPaginationComponent();

        await this.gridReady;
      } else {
        return;
      }
    }

    // `api` becomes available after the grid is ready
    let { api } = this.gridOptions;

    // if there is no `api` yet
    if (!api) {
      const readyResponse = await this.gridReady;
      api = readyResponse.api;
    }

    if (changedProperties.has('alwaysAutoSizeColumns')) {
      this._autoSizeColumns();
    }

    // TODO: consider removing filters from the sidebar if
    // `advancedFilterRenderer` becomes null or undefined
    if (changedProperties.has('advancedFilterRenderer')) {
      this.filterModule.appendFiltersInSideBar();
    }

    if (changedProperties.has('pageSize') && this._resultsPerPageDropdown) {
      this._resultsPerPageDropdown.setAttribute(
        'value',
        this.pageSize.toString()
      );

      // `this.pageSize` is always synced with `gridOptions.paginationPageSize`
      // via a custom property descriptor on gridOptions.
      // NOTE: it seems like AG Grid's change detection doesn't pick up on this
      // change because the value is the always the same, therefore we need to
      // force a re-render by setting the pageSize explicitly to a value that
      // will always differ from `this.pageSize`
      api.paginationSetPageSize(0);
    }

    if (changedProperties.has('server')) {
      this.setServerSideServer(this.server);
    }

    // TODO: this logic seems to be somewhat duplicated with `isLoading` below
    if (changedProperties.has('rowData')) {
      if (this.rowData === undefined) {
        api.showLoadingOverlay();
      }

      if (this.rowData && this.clientSideData) {
        this._updateAgGridClientRowData(this.rowData);
      }
    }

    if (changedProperties.has('isLoading')) {
      if (this.isLoading) {
        api.showLoadingOverlay();
      } else {
        if (this.rowData?.length) {
          api.hideOverlay();
        }

        if (
          this.rowData &&
          this.clientSideData &&
          !changedProperties.has('rowData')
        ) {
          this._updateAgGridClientRowData(this.rowData);
        }
      }
    }

    if (
      changedProperties.has('currentPage') &&
      typeof this.currentPage === 'number'
    ) {
      api.paginationGoToPage(this.currentPage - 1);
    }

    // If fundamental grid properties have changed since the creation of the grid,
    // the columns need to be re-created
    if (
      changedProperties.has('columns') ||
      // the presence of `actionColumnRenderer` will affect the columns
      changedProperties.has('actionColumnRenderer') ||
      changedProperties.has('actionColMinWidth')
    ) {
      if (this.columns) {
        // NOTE: `_normalizeColumns` mutates `this.columns` to be consistent
        // with how AG Grid handles `gridOptions`. See `this._gridOptions`
        this._normalizeColumns(this.columns);
      }

      // this just tells AG Grid to re-draw using the updated definitions
      api.setColumnDefs(this.columns);
    }

    if (changedProperties.has('paginationPrefix')) {
      if (!this._paginationPrefixElement) {
        this._appendPaginationComponent();
      }
      if (this._paginationPrefixElement) {
        this._paginationPrefixElement.textContent = this.paginationPrefix ?? '';
      }
    }

    if (
      changedProperties.has('resultsPerPageOptions') &&
      this._resultsPerPageDropdown
    ) {
      const dropdownOptions = this.resultsPerPageOptions.map(val => ({
        name: val.toString(),
        value: val.toString(),
      }));

      this._resultsPerPageDropdown.setAttribute(
        'options',
        JSON.stringify(dropdownOptions)
      );
    }

    if (
      changedProperties.has('currentPage') &&
      this._paginationGtpInputElement
    ) {
      if (this.currentPage) {
        this._paginationGtpInputElement.setAttribute(
          'value',
          this.currentPage.toString()
        );
      } else {
        this._paginationGtpInputElement.removeAttribute('value');
      }
    }

    if (changedProperties.has('indefiniteResultsSize')) {
      if (this.indefiniteResultsSize) {
        this._goToPageElement?.remove();
        this._goToPageElement = null;
      } else {
        this._appendPaginationComponent(); // Re-append pagination component if it was removed
      }
    }

    if (
      changedProperties.has('actionColumnRenderer') ||
      changedProperties.has('isRowEditable') ||
      changedProperties.has('isRowDisabled')
    ) {
      // for the action column to re-render
      api.refreshCells({
        force: true,
        columns: [ACTION_COLUMN_ID],
      });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._grid?.destroy();
    this._isDestroyed = true;
    this._actionBarIntersectionObserver?.disconnect();
  }

  // #endregion

  // #region PRIVATE METHODS

  private _updateAgGridContainer() {
    // Manage container classes
    const containerClassEntries = Object.entries({
      'auto-height': this.autoHeight,
      'ag-grid-container': true,
      'ag-theme-alpine': true,
      'ag-theme-katal': true,
      'filter-applied': this.filterModule.isFilterApplied(),
      'bulk-editing-grid': this.editMode === 'bulk',
      'single-selection': this.selection === 'single',
      'multi-selection': this.selection === 'multi',
      'tool-panel-visible': this.toolPanelVisible,
    });

    for (const [className, applies] of containerClassEntries) {
      if (applies) this.gridContainer.classList.add(className);
      else this.gridContainer.classList.remove(className);
    }

    // Manage container inline styles
    if (this.width === undefined && this.gridContainer.style.width) {
      this.gridContainer.style.removeProperty('width');
    } else if (
      this.width !== undefined &&
      this.gridContainer.style.width !== this.width
    ) {
      this.gridContainer.style.width = this.width;
    }

    if (this.height) {
      // Explicit pixel height
      this.gridContainer.style.maxHeight = this.height;
      this.gridContainer.style.height = this.height;
    } else if (this.autoHeight) {
      // Height should shrink to fit contents
      this.gridContainer.style.height = 'auto';
      this.gridContainer.style.maxHeight = 'unset';
    } else if (!this.autoHeight) {
      // Height should use full parent's space
      this.gridContainer.style.height = '100%';
      this.gridContainer.style.maxHeight = 'unset';
    }
  }

  /**
   * Returns whether a column is editable or not using the properties
   * `isRowDisabled` and `isRowEditable`. Note that this method is bound to the
   * current instance so that current values of `isRowDisabled` and
   * `isRowEditable` on the instance are used
   */
  private _columnIsEditable = ({
    node,
    data,
  }: Pick<EditableCallbackParams, 'node' | 'data'>) => {
    // not editable for groups
    if (node?.group) return false;
    // can't edit if there is no data
    if (!data) return false;
    // if `isRowDisabled` is implemented and it returns true,
    // then the row is not editable
    if (this.isRowDisabled?.(data)) return false;
    // if `isRowEditable` is not implemented, then this row is editable
    if (!this.isRowEditable) return true;
    // otherwise, delegate to `isRowEditable`
    return this.isRowEditable(data);
  };

  private _bulkEditFinished = async () => {
    const { api } = await this.gridReady;
    const result = await new Promise<'cancelled' | 'saved'>(resolve => {
      const handler = (e: 'cancelled' | 'saved') => {
        this._bulkEditDone.removeListener(handler);
        resolve(e);
      };

      this._bulkEditDone.addListener(handler);
    });

    // await any final row edits
    const finalEdits = new Promise<void>(resolve => {
      // if there are no editing cells, then resolve immediately
      if (!api.getEditingCells().length) {
        resolve();
        return;
      }

      // otherwise wait for the `rowEditingStopped` event
      const handler = () => {
        api.removeEventListener('rowEditingStopped', handler);
        resolve();
      };
      api.addEventListener('rowEditingStopped', handler);
    });

    api.stopEditing();

    await finalEdits;

    return result;
  };

  /**
   * Returns a promise that resolves when a row edit is finished and includes
   * an array of `CellEditRequestsEvent`s that occurred while waiting for row
   * editing to end as well as a `type`` that states if the user clicked the
   * "Cancel" or "Save" button on the row before the row edit was finished.
   */
  private async _rowEditingStopped(rowId: string) {
    const { api } = await this.gridReady;

    const cellEditRequests: CellEditRequestEvent[] = [];
    const handleCellEditRequest = cellEditRequests.push.bind(cellEditRequests);

    api.addEventListener('cellEditRequest', handleCellEditRequest);

    let type: 'cancelled' | 'saved' | undefined;

    const handleRowEditDone = (params: {
      rowId: string;
      type: 'cancelled' | 'saved';
    }) => {
      if (rowId === params.rowId) {
        type = params.type;
      }
    };
    this._rowEditDone.addListener(handleRowEditDone);

    await new Promise<void>(resolve => {
      const handler = (e: RowEditingStoppedEvent) => {
        if (e.node.id !== rowId) return;
        api.removeEventListener('rowEditingStopped', handler);
        resolve();
      };
      api.addEventListener('rowEditingStopped', handler);
    });

    api.removeEventListener('cellEditRequest', handleCellEditRequest);
    this._rowEditDone.removeListener(handleRowEditDone);

    return { cellEditRequests, type };
  }

  private _autoSizeColumns = debounce(() => {
    const { columnApi, api } = this.gridOptions;
    if (!columnApi || !api || this._isDestroyed) return;

    if (this.alwaysAutoSizeColumns) {
      columnApi.autoSizeAllColumns(
        true // skip headers
      );
    }
  }, 100);

  private _normalizeColumns(columns: DataGridColumn[]) {
    for (const column of columns) {
      // TODO: warn if `cellRenderer` and `renderCustomContent` are both defined
      if (column.renderCustomContent) {
        column.cellRenderer = BaseCellRenderer;
        column.cellRendererParams = {
          ...column.cellRendererParams,
          // binding here in case the user column implementation uses `this`
          renderCustomContent: column.renderCustomContent.bind(column),
        };
      }

      if (
        column.cellRenderer &&
        column.cellRenderer !== AutoWidthAdjustingCellRenderer &&
        typeof column.cellRenderer !== 'string'
      ) {
        column.cellRendererParams = {
          ...column.cellRendererParams,
          renderer: column.cellRenderer,
        };
        column.cellRenderer = AutoWidthAdjustingCellRenderer;
      }

      // disable editing for the primary field column
      // note: the primaryKeyField should not be editable because we use the ID
      // to track changes
      if (column.field === this.primaryKeyField) {
        column.editable = false;
      }

      // if `editable` is explicity `true` then replace it with a callback that
      // considers `isRowEditable` and `isRowDisabled`
      if (column.editable === true) {
        column.editable = this._columnIsEditable;

        // if it exists, use the existing value of `cellClass` otherwise set it to
        // function that adds `kat-editable-cell` if the column is editable
        // https://t.corp.amazon.com/V1079663041/overview
        if (!column.cellClass) {
          column.cellClass = params =>
            this._columnIsEditable(params) ? ['kat-editable-cell'] : undefined;
        }
      }
    }

    // remove any existing selection and action columns.
    //
    // note: we are using `splice` here to mutate the current array in place.
    // we mutate the array (instead of creating a new array) to be consistent
    // with of how ag-grid exposes and consumes `GridOptions`. see _gridOptions.
    //
    // we iterate through the array in reverse to avoid skipping elements due to
    // index changes upon mutation
    for (let i = columns.length - 1; i >= 0; i--) {
      const col = columns[i];
      if (col.colId === ACTION_COLUMN_ID) {
        columns.splice(i, 1);
      }
    }

    if (this.actionColumnRenderer || this.isRowEditingEnabled()) {
      columns.push({
        minWidth: this.actionColMinWidth,
        ...this._actionColumnConfig,
      });
    }
  }

  private _updateAgGridClientRowData(rowData: any[] | undefined) {
    const { api } = this.gridOptions;
    if (!api) return;

    // if the primary key field is not present in the first row, then generate
    // a unique key for each row using the row index
    if (rowData?.length && !(this.primaryKeyField in rowData[0])) {
      for (let rowIndex = 0; rowIndex < rowData.length; rowIndex++) {
        const row = rowData[rowIndex];
        row[this.primaryKeyField] = rowIndex.toString();
      }
    }

    if (rowData) {
      api.setRowData(rowData);
    }

    this.totalRowCount = rowData?.length || 0;
  }

  private _recalculateFilteredRowCount() {
    const { api } = this.gridOptions;
    if (!api) return;

    if (this.isRowGroupPresent() && this.clientSideData) {
      let filteredRowCount = 0;
      api.forEachNode(node => {
        if (node.allChildrenCount) filteredRowCount += node.allChildrenCount;
      });
      this.setFilteredRowCount(filteredRowCount);
    } else if (this.clientSideData) {
      this.setFilteredRowCount(api.getModel().getRowCount());
    } else {
      // for serverside set it in createDatasource getRows
    }
  }

  private _createDatasource(server: DataGridServer) {
    if (server) {
      return {
        // called by the grid when more rows are required
        getRows: async ({
          api,
          request,
          success,
          fail,
        }: IServerSideGetRowsParams) => {
          api.hideOverlay();

          try {
            const requestParams: ServerParamsRequest = { ...request };
            //search option for serverside with the dropdown value as searchOption
            requestParams['customSearch'] = {
              searchTerm: this.searchTerm,
              searchOption: this.selectedSearchOption,
            };
            requestParams.primaryKeyField = this.primaryKeyField;

            requestParams['customFilter'] = Object.keys(
              this.filterModule.externalFilterStore
            );
            const response: PaginatedDataGridData = await server.getRows(
              requestParams
            );

            //if we are doing grouping, and user dont set the primary key
            //setting its value to avoid bug
            if (
              requestParams.rowGroupCols.length > requestParams.groupKeys.length
            ) {
              response.results.forEach(row => {
                if (
                  !row[this.primaryKeyField] ||
                  row[this.primaryKeyField] === 'undefined'
                ) {
                  console.warn(
                    `Please set primaryKeyField: ${this.primaryKeyField} for`,
                    row
                  );
                }
              });
            }

            // This is only here to deep copy the results array to prevent errors that
            // arise when original data is mutated by grid edits
            response.results = JSON.parse(JSON.stringify(response.results));

            if (response.results) {
              if (!response.results || response.results.length === 0) {
                api.showNoRowsOverlay();
              } else {
                api.hideOverlay();
              }

              success({
                rowData: response.results,
                rowCount: response.lastRowIndex,
              });

              if (this.searchTerm) {
                this.serverSideSearchApplied = true;
              } else {
                this.serverSideSearchApplied = false;
              }
              this.setTotalRowCount(response.totalResultsSize);
              this.setFilteredRowCount(response.filteredResultsSize);
            } else {
              console.debug(response);
              // inform grid request failed
              fail();
            }
          } catch (e) {
            console.error(e);
            fail();
          }
        },
      };
    }
    return {
      getRows: () => null,
    };
  }

  private _appendPaginationComponent(): void {
    // Clear existing pagination components
    const resultsPerPage = this.querySelector('.results-per-page');
    const goToPage = this.querySelector('.go-to-page');
    const paginationPrefix = this.querySelector(`.${PAGINATION_PREFIX_CLASS}`);

    if (resultsPerPage) resultsPerPage.remove();
    if (goToPage) goToPage.remove();
    if (paginationPrefix) paginationPrefix.remove();

    this._paginationPrefixElement = null;
    this._resultsPerPageDropdown = null;
    this._paginationGtpInputElement = null;
    this._goToPageElement = null;

    const agPagingRowSummaryPanel = this.querySelector<HTMLElement>(
      '.ag-paging-row-summary-panel'
    );
    const agPagingPanel = this.querySelector<HTMLElement>('.ag-paging-panel');

    // append the pagination prefix
    if (this.paginationPrefix && agPagingRowSummaryPanel) {
      const paginationPrefix = document.createElement('span');
      paginationPrefix.classList.add(PAGINATION_PREFIX_CLASS);
      paginationPrefix.textContent = this.paginationPrefix;
      agPagingRowSummaryPanel.prepend(paginationPrefix);

      this._paginationPrefixElement = paginationPrefix;
    }

    // append the results per page dropdown
    if (agPagingPanel) {
      const resultsPerPage = document.createElement('div');
      resultsPerPage.classList.add('results-per-page');
      resultsPerPage.innerHTML = `
        <div>${getString('dg-results-per-page')}</div>
        <div class="pagination-dropdown-container"></div>
      `;
      const dropdownContainer = resultsPerPage.querySelector(
        '.pagination-dropdown-container'
      ) as HTMLElement; // will always exists due to the above

      const dropDown = document.createElement('kat-dropdown');
      const dropdownOptions = this.resultsPerPageOptions.map(val => ({
        name: val.toString(),
        value: val.toString(),
      }));

      dropDown.setAttribute('options', JSON.stringify(dropdownOptions));
      dropDown.setAttribute('value', this.pageSize.toString());

      dropDown.addEventListener('change', e => {
        const value = Number((e.target as HTMLInputElement).value);
        this.pageSize = value;
      });

      dropdownContainer.append(dropDown);
      agPagingPanel.append(resultsPerPage);

      this._resultsPerPageDropdown = dropDown;
    }

    // don't add the go-to-page input if we have an indefinite result size
    if (!this.indefiniteResultsSize) {
      const agPagingPageSummaryPanel = this.querySelector<HTMLElement>(
        '.ag-paging-page-summary-panel'
      );
      if (agPagingPageSummaryPanel) {
        const goToPage = document.createElement('div');
        goToPage.classList.add('go-to-page');
        goToPage.innerHTML = `
          <div class="pagination-vertical-line"></div>
          <div>${getString('dg-go-to-page')}</div>
        `;
        const paginationGtpInput = document.createElement('kat-input');
        paginationGtpInput.setAttribute('type', 'text');
        paginationGtpInput.setAttribute(
          'kat-aria-label',
          getString('dg-page-number')
        );
        if (typeof this.currentPage === 'number') {
          paginationGtpInput.setAttribute('value', this.currentPage.toString());
        }

        paginationGtpInput.addEventListener('change', e => {
          const value = Number((e.target as HTMLInputElement).value);
          this.currentPage = value;
        });

        goToPage.append(paginationGtpInput);
        agPagingPageSummaryPanel.append(goToPage);

        this._paginationGtpInputElement = paginationGtpInput;
        this._goToPageElement = goToPage;
      }
    }
  }

  /**
   * Creates an alias for a property within the KatDataGrid class. Returns an
   * object with a single key with the value being a property descriptor object
   * that can be spread onto a `PropertyDescriptorMap` in
   * `Object.defineProperties()`. The property descriptor returns the the value
   * of `this[selfName]` when accessed and sets the value of `this[selfName]`
   * when set.
   */
  private _createPropertyAlias<
    TSelfPropName extends keyof KatDataGrid,
    TPropName extends string
  >(selfName: TSelfPropName, name?: TPropName) {
    const property: PropertyDescriptor = {
      // make the property enumerable so that values are copied over when used
      // in a spread operator or when used with `Object.assign`
      enumerable: true,
      get: () => this[selfName],
      set: value => (this[selfName] = value),
    };

    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return { [name || selfName]: property } as {
      [K in TPropName]: PropertyDescriptor;
    };
  }

  /**
   * Similar to `_createPropertyAlias`. A helper that returns an object with a
   * single property descriptor for use directly in `Object.defineProperties()`.
   *
   * Note: for computed properties, the setter will warn if used.
   */
  private _createComputedProperty<TPropName extends string, TReturn>(
    name: TPropName,
    get: () => TReturn
  ): { [K in TPropName]: PropertyDescriptor } {
    const property: PropertyDescriptor = {
      // make the property enumerable so that values are copied over when used
      // in a spread operator or when used with `Object.assign`
      enumerable: true,
      // make the property deletable so that users can control over the property
      // if they really want to. this is more of an escape hatch than anything
      configurable: true,
      get,
      set: value => {
        // Override computed properties if they are explicitly passed. Once this is done the properties
        // are no longer computed, it's up to the consumer to set them correctly.
        delete this._gridOptions[name as any];
        this._gridOptions[name as any] = value;
      },
    };

    // returns the property descriptor as a single property object keyed by the
    // corresponding property name
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return { [name]: property } as {
      [K in TPropName]: PropertyDescriptor;
    };
  }

  /**
   * Internal reference for grid options. This reference will be passed into
   * AG Grid's `Grid` constructor. AG Grid will keep this reference and mutate
   * this object, so we also need to keep a reference to it and delegate all
   * related changes from this component's properties to AG Grid.
   */
  private readonly _gridOptions: GridOptions = Object.defineProperties(
    {
      ...DEFAULT_GRID_OPTIONS,
      defaultColDef: {
        ...DEFAULT_COL_DEF,
        getQuickFilterText: this.getQuickFilterText,
      },
      getRowId: this.getRowId,
      onDisplayedRowsChanged: this.handleDisplayedRowsChanged,
      onFilterChanged: this.handleFilterChanged,
      onFirstDataRendered: this.handleFirstDataRendered,
      onGridReady: this.handleGridReady,
      onGridSizeChanged: this._autoSizeColumns,
      onPaginationChanged: this.handlePaginationChanged,
      onRowSelected: this.selectionModule.onRowSelected,
      onSelectionChanged: this.selectionModule.onSelectionChanged,
      onSortChanged: this._autoSizeColumns,
      onToolPanelVisibleChanged: this.handleToolPanelVisibleChanged,
      onVirtualColumnsChanged: this.handleVirtualColumnsChanged,
      getLocaleText: params => {
        if (strings[`ag-${params.key}`]) {
          return getString(`ag-${params.key}` as keyof typeof strings);
        }

        if (params.defaultValue) {
          // This string hasn't been localized yet, return default value.
          return params.defaultValue;
        }
      },
    },
    {
      ...this._createComputedProperty('cacheBlockSize', () => this.pageSize),
      ...this._createPropertyAlias('columns', 'columnDefs'),
      ...this._createPropertyAlias('detailRowHeight'),
      ...this._createComputedProperty(
        'detailRowAutoHeight',
        () => !this.detailRowHeight
      ),
      ...this._createComputedProperty('domLayout', () =>
        this.autoHeight ? 'autoHeight' : 'normal'
      ),
      ...this._createComputedProperty('editType', () => 'fullRow'),
      ...this._createPropertyAlias('isDebugMode', 'debug'),
      ...this._createComputedProperty('loadingCellRendererParams', () =>
        Object.defineProperties(
          {},
          this._createPropertyAlias('loadingText', 'loadingMessage')
        )
      ),
      ...this._createPropertyAlias('masterDetail'),
      ...this._createComputedProperty('noRowsOverlayComponentParams', () =>
        Object.defineProperties(
          {},
          {
            ...this._createPropertyAlias('filterModule'),
            ...this._createPropertyAlias('noFilteredResultsMessage'),
            ...this._createPropertyAlias('noResultsMessage'),
          }
        )
      ),
      ...this._createComputedProperty(
        'overlayLoadingTemplate',
        () => `
        <div class="dg-overlay-loading-template">
          <div class="dg-overlay-message-wrapper">
            <kat-spinner class="dg-overlay-loading-spinner" size="small" variant="default"></kat-spinner>
            <span class="message">${
              this.loadingText ? this.loadingText : 'Loading...'
            }</span>
            </div>
            <hr>
        </div>
      `
      ),
      ...this._createPropertyAlias('pagination'),
      // this property descriptor is created manually because of the workaround
      // to force AG Grid to re-render with the new page size. see the
      // `updated()` implementation for more details.
      paginationPageSize: {
        enumerable: true,
        get: () => this.pageSize,
        set: value => {
          if (value !== 0) {
            console.warn(
              'Tried to set property `paginationPageSize` but this property is ' +
                'computed and readonly. Set `pageSize` on the element instead.'
            );
          }
        },
      },
      ...this._createComputedProperty('readOnlyEdit', () => true),
      ...this._createComputedProperty('rowClassRules', () => ({
        'disabled-rows': (rowNode: RowNode) =>
          rowNode.data && this.isRowDisabled
            ? this.isRowDisabled(rowNode.data)
            : false,
      })),
      ...this._createComputedProperty('rowModelType', () =>
        this.clientSideData ? undefined : 'serverSide'
      ),
      ...this._createComputedProperty('rowSelection', () =>
        this.selection === 'none'
          ? null
          : this.selection === 'multi'
          ? 'multiple'
          : 'single'
      ),
      ...this._createComputedProperty('serverSideInfiniteScroll', () =>
        this.clientSideData ? undefined : true
      ),
      ...this._createComputedProperty(
        'suppressClickEdit',
        () => this.editMode !== 'bulk'
      ),
      ...this._createComputedProperty(
        'suppressPaginationPanel',
        () => !!this.indefiniteResultsSize
      ),
    }
  );

  // #endregion
}
